import ProfilesResource from 'flow/api/profiles';
import Actions from 'flow/actions';
import {
    networkErrorDispatcher,
    httpGetErrorDispatcher,
    setSnackbar,
} from 'flow/actions/app-actions';
import { getCalendar } from 'flow/actions/extension-actions';
import { history } from 'flow/configure-store';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import { HTTPStatusCodes } from 'flow/utils';

export const fetchProfiles = (extensionKey) => {
    return (dispatch) => {
        ProfilesResource.getProfiles(
            {
                onResult(json) {
                    dispatch(receiveProfiles(extensionKey, json));
                },
                onClientError(request) {
                    if (request.status === HTTPStatusCodes.FORBIDDEN_403) {
                        dispatch(receiveProfiles(extensionKey, []));
                    }
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            extensionKey,
        );
    };
};
export const updateProfile =
    (extensionKey, profileDTO, callBack) => (dispatch) => {
        ProfilesResource.updateProfile(
            {
                onResult(json) {
                    dispatch(receiveProfile(extensionKey, json));
                    callBack?.();
                },

                onClientError(request) {
                    if (request.status === HTTPStatusCodes.FORBIDDEN_403) {
                        dispatch(setCallRecordingPermissionError);
                    }
                },

                onError: networkErrorDispatcher(dispatch),
            },
            extensionKey,
            profileDTO,
        );
    };

export const updateProfiles =
    (extensionKey, profileDTOs, callBack) => (dispatch) => {
        ProfilesResource.updateProfiles(
            {
                onResult(json) {
                    dispatch(receiveProfiles(extensionKey, json));
                    callBack?.();
                },

                onClientError(request) {
                    if (request.status === HTTPStatusCodes.FORBIDDEN_403) {
                        dispatch(setCallRecordingPermissionError);
                    }
                },

                onError: networkErrorDispatcher(dispatch),
            },
            extensionKey,
            profileDTOs,
        );
    };

export const createProfile = (extensionKey, profileDTO) => (dispatch) => {
    ProfilesResource.createProfile(
        {
            onResult(json) {
                dispatch(notifyNewProfileCreated);
                dispatch(receiveCreateProfile(extensionKey, json));
                history.push(
                    generatePath(routes['settings.profiles.profileKey'], {
                        extensionKey,
                        profileKey: json.key,
                    }),
                );
            },

            onError: networkErrorDispatcher(dispatch),
        },
        extensionKey,
        profileDTO,
    );
};

/**
 * Temporary solution for createProfile(), uses same functionality except for redirecting user back to old settings page
 * @param extensionKey
 * @param profileDTO
 * @returns {(function(*): void)|*}
 */
export const createProfileTW = (extensionKey, profileDTO) => (dispatch) => {
    ProfilesResource.createProfile(
        {
            onResult(json) {
                dispatch(notifyNewProfileCreated);
                dispatch(receiveCreateProfile(extensionKey, json));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        extensionKey,
        profileDTO,
    );
};

export const removeProfile =
    (extensionKey, profileKey, profileDescription) => (dispatch) => {
        ProfilesResource.deleteProfile(
            {
                onResult(/**/) {
                    dispatch(receiveRemoveProfile(extensionKey, profileKey));
                    dispatch(
                        setSnackbar({
                            i18nextMessage: 'profile.remove',
                            i18nextMessageProps: { profileDescription },
                        }),
                    );
                },

                onError: networkErrorDispatcher(dispatch),
            },
            extensionKey,
            profileKey,
        );
    };

export const fetchReferalSounds = (entityKey) => (dispatch) => {
    ProfilesResource.listReferEntries({
        onResult(json) {
            dispatch(receiveReferalEntries(entityKey, json));
        },
        onError: httpGetErrorDispatcher(dispatch),
    });
};
export const fetchMainNumbers = (entityKey) => (dispatch) => {
    ProfilesResource.listMainNumbers({
        onResult(json) {
            dispatch(receiveMainNumbers(entityKey, json));
        },
        onError: httpGetErrorDispatcher(dispatch),
    });
};

export const listSMSSenderNameOptions = (entityKey) => (dispatch) => {
    ProfilesResource.listSMSSenderNameOptions({
        onResult(json) {
            dispatch(receiveSMSSenderNames(entityKey, json));
        },
        onError: httpGetErrorDispatcher(dispatch),
    });
};

export const toggleAutomeeting =
    (extensionKey, calendar, value) => (dispatch) => {
        const updatedCalendar = calendar.set('autoMeeting', value);
        dispatch(updateCalendar(extensionKey, updatedCalendar));
    };

export const updateCalendar = (extensionKey, calendarDTO) => (dispatch) => {
    ProfilesResource.updateCalendar(
        {
            onResult(json) {
                dispatch(receiveCalendarUpdate(extensionKey, json));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        extensionKey,
        calendarDTO,
    );
};

export const clearCalendarEvents = (extensionKey) => (dispatch) => {
    ProfilesResource.clearCalendarEvents(
        {
            onResult(/*json*/) {
                dispatch(getCalendar(extensionKey));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        extensionKey,
    );
};

const notifyNewProfileCreated = {
    type: Actions.NOTIFY_NEW_PROFILE_CREATED,
};

const receiveCalendarUpdate = (extensionKey, json) => {
    return {
        type: Actions.RESULT_EXTENSION_CALENDAR,
        extensionKey: extensionKey,
        data: json,
    };
};

const receiveCreateProfile = (extensionKey, json) => {
    return {
        type: Actions.RESULT_PROFILE_CREATE,
        key: extensionKey,
        data: json,
    };
};

const receiveProfile = (extensionKey, json) => {
    return {
        type: Actions.RESULT_PROFILE,
        key: extensionKey,
        data: json,
    };
};

export const removeNotifyNewProfileCreated = {
    type: Actions.REMOVE_NOTIFY_NEW_PROFILE_CREATED,
};

export const setCallRecordingPermissionError = {
    type: Actions.SET_PERMISSION_ERROR,
    key: 'call-recording-error',
};

export const removeCallRecordingPermissionError = {
    type: Actions.REMOVE_PERMISSION_ERROR,
    key: 'call-recording-error',
};

const receiveProfiles = (extensionKey, json) => {
    return {
        type: Actions.RESULT_PROFILES,
        key: extensionKey,
        data: json,
    };
};

const receiveRemoveProfile = (extensionKey, profileKey) => {
    return {
        type: Actions.RESULT_REMOVE_PROFILE,
        key: extensionKey,
        profileKey: profileKey,
    };
};

const receiveReferalEntries = (entityKey, soundList) => ({
    type: Actions.RESULT_REFERAL_ENTRIES,
    key: entityKey,
    data: soundList,
});

const receiveMainNumbers = (entityKey, mainNumbers) => ({
    type: Actions.RESULT_MAIN_NUMBERS,
    key: entityKey,
    data: mainNumbers,
});

const receiveSMSSenderNames = (entityKey, json) => ({
    type: Actions.RESULT_SMSSENDER_NAMES,
    entityKey: entityKey,
    data: json,
});
